/**
 * Parent notice for school-material and voice processing (NFR-05), one entry
 * per notice version. The server names the current version
 * (`app_private.material_consent_policy_version()`, migrations 0010, 0012);
 * the app shows consent only for a version it has the text of, so a parent
 * never agrees to a text they were not shown. Changing anything below means a
 * new version here AND a migration that replaces the version function.
 *
 * Every statement describes implemented behaviour (extract-material,
 * material-learning, assess-reading, migrations 0005–0012) or quotes Google's
 * public documentation as verified in docs/design/school-material-processing-
 * consent-proposal.md §2. Azure Speech's region and Microsoft's data terms
 * were not verified and are therefore not stated. Wording, retention and
 * regions are pending PO approval; this is not legal text.
 */

export interface NoticeSection {
  readonly heading: string
  readonly paragraphs: readonly string[]
}

export interface ConsentNotice {
  readonly version: string
  readonly title: string
  readonly sections: readonly NoticeSection[]
}

const MATERIAL_WHAT: NoticeSection = {
  heading: 'Dữ liệu nào được gửi',
  paragraphs: [
    'Khi con chọn ảnh (PNG, JPEG) hoặc PDF tài liệu học — trang sách, phiếu bài tập, vở ghi — và bấm “Gửi để đọc”, chỉ tệp đó được gửi đi. Ứng dụng không gửi tên, năm sinh hay hồ sơ của con.',
    'Mọi chữ in hoặc viết tay trên trang giấy (ví dụ tên, lớp, trường của con, lời phê của thầy cô) cũng được đọc như các chữ khác. Bố mẹ nên nhắc con che tên trước khi chụp.',
  ],
}

const MATERIAL_WHERE: NoticeSection = {
  heading: 'Gửi tới đâu và để làm gì',
  paragraphs: [
    'Tệp được gửi tới dịch vụ Google Cloud Document AI, xử lý tại khu vực Liên minh châu Âu (EU), chỉ để đọc chữ trong tài liệu. Con xem lại, sửa phần chữ đọc được rồi luyện tập từ nội dung đó.',
    'Google đọc tệp trước, ứng dụng kiểm tra sau: ứng dụng chỉ biết tệp có phải tài liệu học hay không sau khi Google đã đọc chữ. Tệp không phải tài liệu học bị từ chối, nhưng đã được Google xử lý.',
  ],
}

const GOOGLE_DOCS: NoticeSection = {
  heading: 'Theo tài liệu công khai của Google',
  paragraphs: [
    'Dữ liệu gửi đi được xử lý trong bộ nhớ, không lưu xuống ổ đĩa và không dùng để huấn luyện mô hình của Google. Google có ghi tạm một số thông tin về mỗi yêu cầu; Google chưa công bố thời hạn lưu các thông tin này.',
  ],
}

const MATERIAL_KEEPS: readonly string[] = [
  'Tệp ảnh/PDF gốc: không lưu.',
  'Toàn bộ chữ Google đọc được: không lưu; chỉ hiện trên máy để con xem lại.',
  'Phần chữ con đã xác nhận để học: lưu cùng bài học và tự động xóa sau 30 ngày (việc xóa chạy mỗi ngày một lần).',
  'Bài luyện tập, câu trả lời và kết quả học: giữ trong tài khoản gia đình để bố mẹ theo dõi. Bài Toán có thể giữ các phép tính lấy từ tài liệu (ví dụ 1/2 + 1/3).',
  'Mỗi lần gửi: thời gian và kết quả (tài liệu học, chưa chắc chắn, không phải tài liệu học, lỗi), dùng cho giới hạn gửi trong 7 ngày. Hiện các bản ghi này chưa có thời hạn tự xóa.',
]

const NOT_LEGAL: NoticeSection = {
  heading: 'Lưu ý',
  paragraphs: [
    'Thông báo này dùng cho giai đoạn thử nghiệm trong gia đình. Đây không phải văn bản pháp lý và chưa được rà soát pháp lý.',
  ],
}

export const CONSENT_NOTICES: Readonly<Record<string, ConsentNotice>> = {
  'smp-2026-11-03': {
    version: 'smp-2026-11-03',
    title: 'Gửi tài liệu ở trường để ứng dụng đọc chữ',
    sections: [
      MATERIAL_WHAT,
      MATERIAL_WHERE,
      GOOGLE_DOCS,
      { heading: 'Ứng dụng lưu gì và trong bao lâu', paragraphs: MATERIAL_KEEPS },
      {
        heading: 'Rút lại đồng ý',
        paragraphs: [
          'Bố mẹ có thể rút lại đồng ý bất cứ lúc nào tại mục “Gửi tài liệu ở trường” trong khu vực phụ huynh, bằng PIN phụ huynh. Sau đó ứng dụng không gửi thêm tài liệu nào tới Google.',
          'Tài liệu đã gửi đi trước đó không thể thu hồi. Phần chữ đã lưu vẫn được xóa theo thời hạn 30 ngày.',
          'Khi nội dung thông báo này thay đổi (ví dụ đổi nơi xử lý), bố mẹ cần đọc và đồng ý lại.',
        ],
      },
      NOT_LEGAL,
    ],
  },
  // Adds voice recordings for Read Aloud (PO direction integrating KT1).
  'smp-2026-11-08': {
    version: 'smp-2026-11-08',
    title: 'Gửi tài liệu ở trường và giọng đọc để ứng dụng xử lý',
    sections: [
      { ...MATERIAL_WHAT, heading: 'Tài liệu ở trường: dữ liệu nào được gửi' },
      { ...MATERIAL_WHERE, heading: 'Tài liệu ở trường: gửi tới đâu và để làm gì' },
      GOOGLE_DOCS,
      {
        heading: 'Giọng đọc (Đọc to)',
        paragraphs: [
          'Khi con ghi âm mình đọc một câu tiếng Anh và bấm “Gửi bài đọc”, bản ghi âm được gửi tới dịch vụ Azure Speech của Microsoft, chỉ để nhận xét cách con phát âm từng từ trong câu đó. Ứng dụng không gửi tên hay hồ sơ của con.',
          'Ứng dụng chỉ cho con xem tối đa 5 từ nên luyện lại. Ứng dụng không chấm điểm, không xếp trình độ nói và không cho con xem chữ mà dịch vụ nghe được.',
          'Thông báo này chưa nêu khu vực máy chủ và chính sách lưu trữ của Microsoft cho dịch vụ này.',
        ],
      },
      {
        heading: 'Ứng dụng lưu gì và trong bao lâu',
        paragraphs: [
          ...MATERIAL_KEEPS,
          'Bản ghi âm giọng đọc: không lưu trên máy chủ; chỉ giữ trên máy để con nghe lại, cho tới khi con rời màn hình Đọc to.',
          'Kết quả mỗi lần đọc (ngày, câu đã đọc, các từ nên luyện lại): giữ trong tài khoản gia đình để con xem lại. Không lưu chữ mà dịch vụ nghe được.',
        ],
      },
      {
        heading: 'Rút lại đồng ý',
        paragraphs: [
          'Bố mẹ có thể rút lại đồng ý bất cứ lúc nào tại mục “Gửi tài liệu và giọng đọc” trong khu vực phụ huynh, bằng PIN phụ huynh. Sau đó ứng dụng không gửi thêm tài liệu nào tới Google và không gửi thêm giọng đọc nào tới Azure Speech.',
          'Tài liệu hay bản ghi đã gửi đi trước đó không thể thu hồi. Phần chữ tài liệu đã lưu vẫn được xóa theo thời hạn 30 ngày.',
          'Khi nội dung thông báo này thay đổi (ví dụ đổi nơi xử lý), bố mẹ cần đọc và đồng ý lại.',
        ],
      },
      NOT_LEGAL,
    ],
  },
}

export function consentNotice(version: string): ConsentNotice | null {
  return CONSENT_NOTICES[version] ?? null
}
